/* eslint-disable no-unused-vars */
/* eslint-disable no-inline-comments */
const ethers = require("ethers");
const { toEthersBN, toBN, BN } = require("./utils");
const hit = require("./hit");
const IERC20ABI = require("../abis/IERC20.json");
const IUniswapV2RouterABI = require("../abis/IUniswapV2Router02.json");
const ITombSwapV2ABI = require("../abis/ITombSwapV2.json");
const IBeefyVaultABI = require("../abis/IBeefyVaultV6.json");
const IComptrollerABI = require("../abis/IComptroller.json");
const IPriceOracleABI = require("../abis/IPriceOracle.json");
const IcERC20ABI = require("../abis/IcERC20.json");
const BeefyStrategyArtifact = require("../artifacts/BeefyStrategy.json");
const { MAX_AMOUNT } = require("../constants");

// File constants
const
  ADDRESS_ZERO = "0x0000000000000000000000000000000000000000",
  NATIVE_TOKEN_SYMBOL = "FTM",
  SLIPPAGE_TOLERANCE = 0.01;

// TODO: CHANGE ALL THESE CONSTANTS TO DATABASE
const
  supplyTokenSymbol = "USDC",
  supplyiTokenSymbol = "iUSDC",
  borrowTokenSymbol = "WFTM",
  lptSymbol = "LPT";

module.exports = async ({
  positionAddress,
  abi,
  strategyId,
  strategist,
  provider
}) => {
  // Getting strategy data from database
  const strategy = await hit({ url: `/strategies/${strategyId}` });

  // Token addresses
  const supplyTokens = strategy.metadata.ironbankData.supplyPairTokens.map((value, _) => value[0]); // First column of supplyPairTokens.
  const supplyiTokens = strategy.metadata.ironbankData.supplyPairTokens.map((value, _) => value[1]); // Second column of supplyPairTokens.
  const borrowTokens = strategy.metadata.ironbankData.borrowPairTokens.map((value, _) => value[0]); // First column of borrowPairTokens.
  const borrowiTokens = strategy.metadata.ironbankData.borrowPairTokens.map((value, _) => value[1]); // Second column of borrowPairTokens.
  const unitrollerAddress = strategy.metadata.ironbankData.unitroller; // IronBank Unitroller contract
  const token0 = strategy.metadata.beefyData.token0;
  const token1 = strategy.metadata.beefyData.token1;
  const targetDebtRatio = strategy.metadata.debtRatio.target;
  const maxDebtRatio = strategy.metadata.debtRatio.max;
  const minDebtRatio = strategy.metadata.debtRatio.min;
  const pairAddress = strategy.metadata.pair;
  const routerAddress = strategy.metadata.router;

  let supplyToken = supplyTokens[0];
  let supplyiToken = supplyiTokens[0];
  let borrowToken = borrowTokens[0];
  let borrowiToken = borrowiTokens[0];
  let pairToken;
  if (borrowToken.toLowerCase() === token0.toLowerCase()) {
    pairToken = token1;
  } else {
    pairToken = token0;
  }

  // General params
  const wrappedNativeToken = strategy.metadata.wrappedNativeToken;
  const router = strategy.metadata.router;

  // Beefy params
  const lpToken = strategy.metadata.beefyData.lpToken;
  const zap = strategy.metadata.beefyData.zap;
  const vault = strategy.metadata.beefyData.vault;

  // Provider
  if (provider === undefined) {
    provider = strategist.provider;
  }

  // Getting existing contracts
  const IERC20 = new ethers.Contract(ADDRESS_ZERO, IERC20ABI.abi, provider);
  const RouterContract = new ethers.Contract(routerAddress, IUniswapV2RouterABI.abi, provider);
  const PairContract = new ethers.Contract(pairAddress, ITombSwapV2ABI, provider);
  const BeefyVaultContract = new ethers.Contract(vault, IBeefyVaultABI, provider);
  const Unitroller = new ethers.Contract(unitrollerAddress, IComptrollerABI.abi, provider);
  const PriceOracle = new ethers.Contract(await Unitroller.oracle(), IPriceOracleABI.abi, provider);
  const IcERC20 = new ethers.Contract(ADDRESS_ZERO, IcERC20ABI.abi, provider);

  // ERC20 Token decimals
  const supplyDecimals = await IERC20.attach(supplyToken).decimals();
  const borrowDecimals = await IERC20.attach(borrowToken).decimals();

  // Getting position contract
  const PositionContract = new ethers.Contract(positionAddress, abi || BeefyStrategyArtifact.abi, provider);

  // Opens the position borrowing the amount for a target debt ratio
  const openPosition = async (amount, { gasPrice, gasLimit } = {}) => {
    const assembleParams = await buildAssembleParams(amount);

    const assembleTx = await PositionContract.connect(strategist).assemble(...assembleParams, { gasPrice, gasLimit });
    await assembleTx.wait();
  };

  const closePosition = async (amount, { gasPrice, gasLimit } = {}) => {
    const disassembleParams = await buildDisassembleParams(amount);

    const disassembleTx = await PositionContract.connect(strategist).disassemble(...disassembleParams, { gasPrice, gasLimit });
    await disassembleTx.wait();
  };

  const shouldRebalance = async () => {
    const { debtRatio } = await getDebtInfo();

    return (debtRatio > maxDebtRatio) || (debtRatio < minDebtRatio);
  };

  const rebalancePosition = async ({ gasPrice, gasLimit } = {}) => {
    const { usdDebt, usdCollateral, debtRatio } = await getDebtInfo();

    const usdTargetDebt = usdCollateral.times(targetDebtRatio);

    if (debtRatio >= maxDebtRatio) {
      const usdToRepay = usdDebt - usdTargetDebt;

      // USD -> supplyToken
      const { supplyTokenPrice } = await getOraclePrices();
      const supplyTokenAmount = BN(usdToRepay).div(supplyTokenPrice).scaleUp(supplyDecimals);

      const disassembleParams = await buildDisassembleParams(supplyTokenAmount);

      const disassembleTx = await PositionContract.connect(strategist).disassemble(...disassembleParams, { gasPrice, gasLimit });
      await disassembleTx.wait();
    }
    else if (debtRatio <= minDebtRatio) {
      const usdToBorrow = usdTargetDebt - usdDebt;

      // USD -> borrowToken
      const { borrowTokenPrice } = await getOraclePrices();
      const borrowTokenAmount = BN(usdToBorrow).div(borrowTokenPrice).scaleUp(borrowDecimals);

      const assembleWithBorrowParams = await buildAssembleWithBorrowParams(borrowTokenAmount);

      const assembleTx = await PositionContract.connect(strategist).assembleWithBorrow(...assembleWithBorrowParams, { gasPrice, gasLimit });
      await assembleTx.wait();
    }
  };

  const supply = async (amount, { gasPrice, gasLimit } = {}) => {
    const supplyTx = await PositionContract.connect(strategist).supply(supplyToken, amount, { gasPrice, gasLimit });
    await supplyTx.wait();
  };

  const redeem = async (collateralPercentage, { gasPrice, gasLimit } = {}) => {
    const supplyiTokenBalance = await IERC20.attach(supplyiToken).balanceOf(positionAddress);

    const redeemTx = await PositionContract.connect(strategist).redeemCToken(supplyToken, toEthersBN(BN(supplyiTokenBalance.toString()).times(collateralPercentage)), { gasPrice, gasLimit });
    await redeemTx.wait();
  };

  const redeemAll = async ({ gasPrice, gasLimit } = {}) => {
    const supplyiTokenBalance = await IERC20.attach(supplyiToken).balanceOf(positionAddress);

    const redeemTx = await PositionContract.connect(strategist).redeemCToken(supplyToken, supplyiTokenBalance, { gasPrice, gasLimit });
    await redeemTx.wait();
  };

  const withdrawAll = async(owner, to, { gasPrice, gasLimit } = {}) => {
    const supplyTokenBalance = await IERC20.attach(supplyToken).balanceOf(positionAddress);

    const withdrawTx = await PositionContract.connect(owner).withdraw(supplyToken, supplyTokenBalance, to, { gasPrice, gasLimit });
    await withdrawTx.wait();
  };

  const redeemAndWithdrawAll = async(owner, to, { gasPrice, gasLimit } = {}) => {
    const withdrawTx = await PositionContract.connect(owner).redeemAndWithdrawAll(supplyToken, to, { gasPrice, gasLimit });
    await withdrawTx.wait();
  };

  const withdraw = async(owner, amount, to, { gasPrice, gasLimit } = {}) => {
    const withdrawTx = await PositionContract.connect(owner).withdraw(supplyToken, amount, to, { gasPrice, gasLimit });
    await withdrawTx.wait();
  };

  const printBalances = async () => {
    const [nativeAmount, supplyTokenAmount, borrowTokenAmount, mooTokenAmount, moo2lpt, mooDecimals, iTokenAmount, marketValue] = await Promise.all([
      provider.getBalance(positionAddress),
      IERC20.attach(supplyToken).balanceOf(positionAddress),
      IERC20.attach(borrowToken).balanceOf(positionAddress),
      IERC20.attach(vault).balanceOf(positionAddress),
      BeefyVaultContract.getPricePerFullShare(),
      BeefyVaultContract.decimals(),
      IERC20.attach(supplyiToken).balanceOf(positionAddress),
      getMarketValue(),
    ]);

    const lpts = BN(mooTokenAmount.toString()).times(BN(moo2lpt.toString()).scaleDown(mooDecimals));

    const table = [];
    const row = {};
    row[NATIVE_TOKEN_SYMBOL] = nativeAmount.toString();
    row[supplyTokenSymbol] = supplyTokenAmount.toString();
    row[supplyiTokenSymbol] = iTokenAmount.toString();
    row[borrowTokenSymbol] = borrowTokenAmount.toString();
    row[lptSymbol] = toEthersBN(lpts).toString();
    row["USD"] = marketValue.toFixed(2);
    table.push(row);
    console.table(table);
  };

  const getDebtInfo = async () => {
    const { borrowTokenPrice, supplyTokenPrice } = await getOraclePrices();

    const iSupplyToken = IcERC20.attach(supplyiToken);
    const iBorrowToken = IcERC20.attach(borrowiToken);

    // Calculate debt value in USD
    const borrowTokenDebt = BN((await iBorrowToken.borrowBalanceCurrent(positionAddress)).toString());
    const usdDebt = borrowTokenDebt.times(borrowTokenPrice).scaleDown(borrowDecimals);

    // Calculate collateral value in USD
    const iSupplyTokenBalance = BN((await iSupplyToken.balanceOf(positionAddress)).toString());
    const iSupplyTokenExchangeRate = BN((await iSupplyToken.exchangeRateStored()).toString());
    // iToken (exchangeRate)-> token (oracle)-> USD
    const usdCollateral = iSupplyTokenBalance
      .times(iSupplyTokenExchangeRate.scaleDown(18))
      .times(supplyTokenPrice).scaleDown(supplyDecimals);

    const debtRatio = usdDebt.div(usdCollateral);

    const liquidity = BN((await Unitroller.getAccountLiquidity(positionAddress))[1].toString());
    const usdLiquidity = liquidity.scaleDown(18);

    return {
      usdDebt,
      usdCollateral,
      debtRatio,
      usdLiquidity
    };
  };

  const printDebtInfo = async () => {
    const { usdDebt, usdCollateral, debtRatio, usdLiquidity } = await getDebtInfo();

    console.log("Debt info:");
    console.table([{
      Collateral: `U$S ${usdCollateral.toFixed(2)}`,
      Debt: `U$S ${usdDebt.toFixed(2)}`,
      Liquidity: `U$S ${usdLiquidity.toFixed(2)}`,
      DebtRatio: `${debtRatio.times(100).toFixed(2)}%`
    }]);
  };

  const getOraclePrices = async () => {
    const borrowTokenPrice = BN((await PriceOracle.getUnderlyingPrice(borrowiToken)).toString()).scaleDown(borrowDecimals);
    const supplyTokenPrice = BN((await PriceOracle.getUnderlyingPrice(supplyiToken)).toString()).scaleDown(2 * borrowDecimals - supplyDecimals);

    return {
      borrowTokenPrice,
      supplyTokenPrice
    };
  };

  const getMarketValue = async () => {
    const [ mooTokenAmount, moo2lpt, mooDecimals, lptsSupply, lpReserves, borrowiTokenPrice, { usdDebt, usdCollateral } ] = await Promise.all([
      IERC20.attach(vault).balanceOf(positionAddress),
      BeefyVaultContract.getPricePerFullShare(),
      BeefyVaultContract.decimals(),
      PairContract.totalSupply(),
      PairContract.getReserves(),
      PriceOracle.getUnderlyingPrice(borrowiToken),
      getDebtInfo(),
    ]);

    const lpts = BN(mooTokenAmount.toString()).times(BN(moo2lpt.toString()).scaleDown(mooDecimals));
    const borrowTokenPrice = BN(borrowiTokenPrice.toString()).scaleDown(borrowDecimals);

    // Getting borrowToken equivalent of LPTs to disassemble.
    let borrowTokenReserves;
    if (borrowToken.toLowerCase() === token0.toLowerCase()) {
      borrowTokenReserves = toBN(lpReserves[0]);

    } else if (borrowToken.toLowerCase() === token1.toLowerCase()) {
      borrowTokenReserves = toBN(lpReserves[1]);
    }

    // Twice because approximately the same amount is returned in pairToken
    const borrowTokenAmount = borrowTokenReserves.times(lpts).div(toBN(lptsSupply)).times(2);

    const usdInvested = BN(borrowTokenAmount.toString()).scaleDown(borrowDecimals).times(borrowTokenPrice);
    const marketValue = usdCollateral.minus(usdDebt).plus(usdInvested);
    return marketValue;
  };

  const getCostOfInvestment = async () => {
    const { usdCollateral } = await getDebtInfo();

    return usdCollateral;
  };

  const getValueInvestedInBeefy = async () => {
    const [lptsSupplied, lptsWithdrawn, lptsSupply, lpReserves, borrowiTokenPrice] = await Promise.all([
      PositionContract.lptsSupplied(),
      PositionContract.lptsWithdrawn(),
      PairContract.totalSupply(),
      PairContract.getReserves(),
      PriceOracle.getUnderlyingPrice(borrowiToken),
    ]);

    const lptsInvested = toBN(lptsSupplied).minus(toBN(lptsWithdrawn));
    const borrowTokenPrice = BN(borrowiTokenPrice.toString()).scaleDown(borrowDecimals);

    // Getting borrowToken equivalent of LPTs to disassemble.
    let borrowTokenReserves;
    if (borrowToken.toLowerCase() === token0.toLowerCase()) {
      borrowTokenReserves = toBN(lpReserves[0]);

    } else if (borrowToken.toLowerCase() === token1.toLowerCase()) {
      borrowTokenReserves = toBN(lpReserves[1]);
    }

    const borrowTokenAmount = borrowTokenReserves.times(lptsInvested).div(toBN(lptsSupply)).times(2);
    const usdInvested = BN(borrowTokenAmount.toString()).scaleDown(borrowDecimals).times(borrowTokenPrice);
    return usdInvested;
  };

  const getOwnership = async () => {
    const [ mooTokenAmount, moo2lpt, mooDecimals, lptsSupply] = await Promise.all([
      IERC20.attach(vault).balanceOf(positionAddress),
      BeefyVaultContract.getPricePerFullShare(),
      BeefyVaultContract.decimals(),
      PairContract.totalSupply(),
    ]);

    const lpts = BN(mooTokenAmount.toString()).times(BN(moo2lpt.toString()).scaleDown(mooDecimals));
    const ownership = lpts.div(toBN(lptsSupply));
    return ownership;
  };

  const getSupplyTokens = () => supplyTokens;

  const setSupplyToken = (tokenAddress) => {
    if (supplyTokens.includes(tokenAddress)) {
      supplyToken = tokenAddress;
    }
  };

  const getBorrowTokens = () => borrowTokens;

  const setBorrowToken = (tokenAddress) => {
    if (borrowTokens.includes(tokenAddress)) {
      borrowToken = tokenAddress;
    }
  };

  const getSupplyiTokens = () => supplyiTokens;

  const setSupplyiToken = (tokenAddress) => {
    if (supplyiTokens.includes(tokenAddress)) {
      supplyiToken = tokenAddress;
    }
  };

  const getBorrowiTokens = () => borrowiTokens;

  const setBorrowiToken = (tokenAddress) => {
    if (borrowiTokens.includes(tokenAddress)) {
      borrowiToken = tokenAddress;
    }
  };

  const getIdleBalance = async () => {
    const balance = await IERC20.attach(supplyToken).balanceOf(positionAddress);
    return BN(balance.toString());
  };

  const buildAssembleParams = async (amount) => {
    const { borrowTokenPrice, supplyTokenPrice } = await getOraclePrices();
    const priceConversion = supplyTokenPrice / borrowTokenPrice;

    const borrowAmount = BN(amount).times(targetDebtRatio * priceConversion).scaleUp(borrowDecimals - supplyDecimals);
    const withBorrowParams = await buildAssembleWithBorrowParams(borrowAmount);

    return [
      supplyToken,
      toEthersBN(amount),
      ...withBorrowParams
    ];
  };

  const buildAssembleWithBorrowParams = async (borrowAmount) => {
    const amountIn = borrowAmount.div(2);

    const desiredTokenOut = (await RouterContract.getAmountsOut(
      toEthersBN(amountIn),
      [borrowToken, pairToken]
    ))[1];

    const desiredTokenOutMin = toBN(desiredTokenOut).times(1 - SLIPPAGE_TOLERANCE);

    return [
      toEthersBN(borrowAmount),
      toEthersBN(desiredTokenOutMin), // tokenAmountOutMin
    ];
  };

  const buildDisassembleParams = async (amount) => {
    const [mooTokenAmount, moo2lpt, mooDecimals, totalLpts, lpReserves] = await Promise.all([
      IERC20.attach(vault).balanceOf(positionAddress),
      BeefyVaultContract.getPricePerFullShare(),
      BeefyVaultContract.decimals(),
      PairContract.totalSupply(),
      PairContract.getReserves(),
    ]);

    // Getting borrowToken equivalent of LPTs to disassemble.
    let borrowTokenReserves;
    if (borrowToken.toLowerCase() === token0.toLowerCase()) {
      borrowTokenReserves = toBN(lpReserves[0]);

    } else if (borrowToken.toLowerCase() === token1.toLowerCase()) {
      borrowTokenReserves = toBN(lpReserves[1]);
    }

    let mooToDisassemble;
    let borrowTokenToDisassembleMin;
    let repayAmount;
    // If no amount is given, disassembling full amount in position.
    if (amount === undefined) {
      mooToDisassemble = mooTokenAmount;

      // LPTs to disassemble.
      const lpts = BN(mooToDisassemble.toString()).times(BN(moo2lpt.toString()).scaleDown(mooDecimals));
      const borrowTokenToDisassemble = borrowTokenReserves.times(lpts).div(toBN(totalLpts));
      borrowTokenToDisassembleMin = borrowTokenToDisassemble.times(1 - SLIPPAGE_TOLERANCE);

      // Repay all debt
      repayAmount = toBN(MAX_AMOUNT);
    } else {
      // Convert supply token amount to borrow token amount, which is going to be repayed
      const { borrowTokenPrice, supplyTokenPrice } = await getOraclePrices();
      const priceConversion = supplyTokenPrice / borrowTokenPrice;
      repayAmount = amount.times(priceConversion).scaleUp(borrowDecimals - supplyDecimals);

      // To get a total of `repayAmount` borrowTokens, I calculate with amount/2 since the other half will be given in the other token in the pair, and then swapped
      const amountForReserves = repayAmount.div(2);

      // Calculate amount needed of LPTs for the given amount of borrowToken
      const lpts = toBN(totalLpts).times(amountForReserves).div(borrowTokenReserves);
      mooToDisassemble = toEthersBN(lpts.times(1 + SLIPPAGE_TOLERANCE).div(BN(moo2lpt.toString()).scaleDown(mooDecimals)));
      borrowTokenToDisassembleMin = amountForReserves;
    }

    return [
      mooToDisassemble, // withdrawAmountInMooToken
      toEthersBN(borrowTokenToDisassembleMin), // desiredTokenOutMin
      supplyToken, // tokenToWithdraw
      toEthersBN(repayAmount), // repayAmount
    ];
  };

  const buildWithdrawTransaction = async ({ asset, amount, to }, opts = {}) => PositionContract.populateTransaction.withdraw(
    asset,
    toEthersBN(amount),
    to,
    opts
  );

  const buildDisassembleTransaction = async (opts = {}) => {
    const underlyingBalance = await getMarketValue();
    const disassembleParams = await buildDisassembleParams(underlyingBalance.scaleUp(supplyDecimals).times(0.99)); // here we are doing 1 USDC = $1
    return PositionContract.populateTransaction.disassemble(...disassembleParams);
  };

  return {
    openPosition,
    closePosition,
    supply,
    redeem,
    redeemAll,
    withdrawAll,
    redeemAndWithdrawAll,
    withdraw,
    shouldRebalance,
    rebalancePosition,
    getSupplyTokens,
    setSupplyToken,
    getBorrowTokens,
    setBorrowToken,
    getSupplyiTokens,
    setSupplyiToken,
    getBorrowiTokens,
    setBorrowiToken,
    getMarketValue,
    getOwnership,
    getValueInvestedInBeefy,
    getCostOfInvestment,
    getDebtInfo,
    printDebtInfo,
    printBalances,
    getIdleBalance,
    buildWithdrawTransaction,
    buildDisassembleTransaction,
    borrowDecimals,
    supplyDecimals,
    supplySymbol: supplyTokenSymbol,
    borrowSymbol: borrowTokenSymbol,
    provider,
    position: PositionContract
  };
};
